import {
  FC, memo, ReactNode, useId,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';

import { CardErrorState } from '@/components';
import { useIsMobile } from '@/hooks';

import { chartColors, colors } from '../../../config/colors';
import { ChartLoadingIcon } from '../../Loaders';
import { EmptyState } from '../EmptyState';
import { ChartRow } from '../types';

interface HsAreaChartProps {
  chartData: ChartRow[];
  eventKey?: string;
  isLoading?: boolean;
  error?: any;
  onRetry?: () => void;
  /** Stopka renderowana wewnątrz karty wykresu, pod wykresem. */
  footer?: ReactNode;
  /** Kolorystyka wykresu: 'brand' = zielony (domyślnie), 'ink' = monochromatyczny czarny. */
  tone?: 'brand' | 'ink';
  /**
   * 'default' = gruba linia z gradientem, kropki, cień (dashboard).
   * 'minimal' = cienka jednolita linia, subtelne wypełnienie, bez kropek/cienia
   * (widok /analytics — styl „clean" GA/Cloudflare).
   */
  variant?: 'default' | 'minimal';
  /** Nadpisanie wysokości wykresu (px, desktop). */
  height?: number;
  /** Własny kolor linii i wypełnienia (nadpisuje `tone`), np. różne kolory wykresów w analityce. */
  color?: string;
}

// Gradient kolorów Sidebara: linear-gradient(180deg, #047857 0%, #064e3b 100%)
const BRAND_GRADIENT = {
  start: '#047857',
  end: '#064e3b',
  light: '#059669',
} as const;

// Monochromatyczny (czarny) wariant — dla dashboardu. W trybie ciemnym jaśnieje,
// żeby linia była czytelna na ciemnym tle.
const inkGradient = (isDark: boolean) => (isDark
  ? { start: '#e5e7eb', end: '#9ca3af', light: '#f3f4f6' }
  : { start: '#111827', end: '#374151', light: '#1f2937' });

const EVENT_NAMES: Record<string, string> = {
  BUSINESS_CARD_SCREEN_OPENED: 'Wizytówki',
  ATTRACTION_SCREEN_OPENED: 'Atrakcje',
  MAP_SCREEN_OPENED: 'Mapa',
  HOTEL_EVENTS_SCREEN_OPENED: 'Wydarzenia',
  SPECIAL_OFFER_SCREEN_OPENED: 'Oferty specjalne',
  WELCOME_SCREEN_START_BUTTON_TAPPED: 'Rozpoczęte sesje',
  total: 'Wszystkie interakcje',
  average: 'Średnia',
};

const CustomTooltip: FC<any> = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    const data = payload[0];

    return (
      <div
        className="backdrop-blur-sm px-4 py-3 rounded-md shadow-lg border"
        style={{
          background: isDark ? 'rgba(36, 42, 51, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#374151' : '#dcdfe4',
        }}
      >
        <p className="text-sm font-light mb-1" style={{ color: isDark ? '#f8fafc' : '#111827' }}>{label}</p>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: data.stroke }}
          />
          <p className="text-sm font-medium" style={{ color: data.stroke }}>
            {EVENT_NAMES[data.dataKey] || data.dataKey}
            :
            {' '}
            <span className="font-normal">{data.value?.toLocaleString()}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const HsAreaChartComponent: FC<HsAreaChartProps> = ({
  chartData,
  eventKey = 'total',
  isLoading,
  error,
  onRetry,
  footer,
  tone = 'brand',
  variant = 'default',
  height,
  color,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  // eslint-disable-next-line no-nested-ternary
  const palette = color
    ? { start: color, end: color, light: color }
    : (tone === 'ink' ? inkGradient(isDark) : BRAND_GRADIENT);
  /* Unikalne id gradientów: kilka wykresów na stronie nie może dzielić tych samych <defs>,
     inaczej wszystkie biorą kolor z pierwszego. */
  const uid = useId().replace(/:/g, '');
  const areaGradientId = `areaGradient-${uid}`;
  const strokeGradientId = `strokeGradient-${uid}`;
  const shadowId = `areaShadow-${uid}`;
  const isMobile = useIsMobile();
  const isMinimal = variant === 'minimal';
  const hasData = chartData && chartData.length > 0;
  const hasNonZeroData = hasData && chartData.some((row) => Number(row[eventKey]) > 0);

  const gridColor = isDark ? '#374151' : '#eef1f4';
  const axisColor = isDark ? '#9ca3af' : '#9ca3af';

  const desktopHeight = height ?? (isMinimal ? 260 : 400);
  const chartHeight = isMobile ? (isMinimal ? 200 : 250) : desktopHeight;
  // W wariancie minimal wykres zaczyna się przy lewej krawędzi kontenera: oś Y tylko
  // tak szeroka, jak etykiety, bez marginesu i bez wewnętrznego paddingu osi X.
  const chartMargin = isMobile
    ? { top: 10, right: 10, left: -20, bottom: 10 }
    : { top: 20, right: 30, left: isMinimal ? -16 : 0, bottom: 20 };
  const yAxisWidth = isMobile ? 30 : (isMinimal ? 44 : 60);
  const xAxisPadding = isMinimal
    ? { left: 0, right: isMobile ? 5 : 20 }
    : { left: isMobile ? 5 : 20, right: isMobile ? 5 : 20 };

  if (isLoading) {
    return (
      <div
        className="w-full flex flex-col items-center justify-center rounded-md"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <ChartLoadingIcon size={isMobile ? 'sm' : 'lg'} />
        <p className="mt-4 text-xs sm:text-sm" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{t('dashboard.loadingChartData')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="w-full flex items-center justify-center rounded-md border"
        style={{
          height: isMobile ? '300px' : '445px',
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <CardErrorState
          title={t('dashboard.chartError', 'Nie udało się wczytać wykresu')}
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (!hasNonZeroData) {
    return (
      <div
        className="w-full h-full rounded-md p-3 sm:p-6 shadow-sm border"
        style={{
          background: theme.colors.chart.background,
          borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
        }}
      >
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      className={isMinimal ? 'w-full h-full' : 'w-full h-full rounded-md p-3 sm:p-6 shadow-sm border'}
      style={isMinimal ? undefined : {
        background: theme.colors.chart.background,
        borderColor: isDark ? '#374151' : 'rgba(226, 232, 240, 0.5)',
      }}
    >

      <ResponsiveContainer width="100%" height={chartHeight}>
        <AreaChart
          data={chartData}
          margin={chartMargin}
        >
          <defs>
            {/* Gradient dla wypełnienia (kolory Sidebara) — subtelniejszy w wariancie minimal. */}
            <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
              {isMinimal ? (
                <>
                  <stop offset="0%" stopColor={palette.light} stopOpacity={0.16} />
                  <stop offset="100%" stopColor={palette.light} stopOpacity={0} />
                </>
              ) : (
                <>
                  <stop offset="0%" stopColor={palette.start} stopOpacity={0.8} />
                  <stop offset="50%" stopColor={palette.start} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={palette.end} stopOpacity={0.1} />
                </>
              )}
            </linearGradient>

            {/* Gradient dla linii */}
            <linearGradient id={strokeGradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={palette.end} stopOpacity={1} />
              <stop offset="50%" stopColor={palette.start} stopOpacity={1} />
              <stop offset="100%" stopColor={palette.light} stopOpacity={1} />
            </linearGradient>

            {/* Cień */}
            <filter id={shadowId} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor={palette.start} floodOpacity={chartColors.shadow.opacity} />
            </filter>
          </defs>

          <CartesianGrid
            strokeDasharray={isMinimal ? '0' : '3 3'}
            stroke={gridColor}
            vertical={false}
            style={{ opacity: isMinimal ? 1 : 0.5 }}
          />

          <XAxis
            dataKey="name"
            stroke={axisColor}
            style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
            axisLine={{ stroke: gridColor }}
            tickLine={{ stroke: gridColor }}
            height={chartData.length > 14 ? 12 : (isMobile ? 40 : 60)}
            padding={xAxisPadding}
            tick={chartData.length > 14 ? false : undefined}
            interval={chartData.length > 30 ? 'preserveStartEnd' : (isMobile ? 1 : 0)}
          />

          <YAxis
            stroke={axisColor}
            style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 500 }}
            axisLine={{ stroke: gridColor }}
            tickLine={{ stroke: gridColor }}
            width={yAxisWidth}
          />

          <Tooltip content={<CustomTooltip isDark={isDark} />} />

          <Area
            type="monotone"
            dataKey={eventKey}
            stroke={isMinimal ? palette.light : `url(#${strokeGradientId})`}
            strokeWidth={isMinimal ? 1.75 : 3}
            fill={`url(#${areaGradientId})`}
            fillOpacity={1}
            isAnimationActive={false}
            animationDuration={1000}
            animationEasing="ease-in-out"
            dot={isMinimal ? false : {
              r: 4,
              fill: palette.start,
              strokeWidth: 2,
              stroke: isDark ? '#1a1f26' : colors.white,
            }}
            activeDot={isMinimal ? {
              r: 3,
              fill: palette.light,
              strokeWidth: 2,
              stroke: isDark ? '#1a1f26' : colors.white,
            } : {
              r: 6,
              fill: palette.start,
              strokeWidth: 2,
              stroke: isDark ? '#1a1f26' : colors.white,
              filter: `url(#${shadowId})`,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {footer}
    </div>
  );
};

// ✅ Memo z custom comparison
export const HsAreaChart = memo(HsAreaChartComponent, (prevProps, nextProps) => (
  prevProps.isLoading === nextProps.isLoading
      && prevProps.chartData === nextProps.chartData
      && prevProps.eventKey === nextProps.eventKey
      && prevProps.tone === nextProps.tone
      && prevProps.variant === nextProps.variant
      && prevProps.height === nextProps.height
      && prevProps.color === nextProps.color
));
